import { QueryClient, onlineManager } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { routeTree } from "./routeTree.gen";
import {
  getConnectivity,
  startConnectivityWatch,
  subscribeConnectivity,
} from "./app-bridge/core/connectivity";

/** Läser ut HTTP-status ur ett fel, när det finns ett. */
function statusOf(error: unknown): number | null {
  if (error && typeof error === "object") {
    const value = (error as { status?: unknown; statusCode?: unknown }).status ??
      (error as { statusCode?: unknown }).statusCode;
    if (typeof value === "number") return value;
  }
  const text = error instanceof Error ? error.message : String(error ?? "");
  const match = text.match(/\b(4\d\d|5\d\d)\b/);
  return match ? Number(match[1]) : null;
}

/** Nya försök vid nät- och serverstrul, aldrig vid fel som beror på anropet. */
function shouldRetry(failureCount: number, error: unknown, max: number): boolean {
  if (failureCount >= max) return false;
  const status = statusOf(error);
  if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) return false;
  return true;
}

export const getRouter = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Tappat nät eller trött server: prova om med växande paus.
        retry: (failureCount, error) => shouldRetry(failureCount, error, 3),
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15000),
        // Så fort vakten säger att vi är uppkopplade igen hämtas data om.
        refetchOnReconnect: "always",
      },
      mutations: {
        retry: (failureCount, error) => shouldRetry(failureCount, error, 2),
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
      },
    },
  });

  // React Query ska följa vår egen uppkopplingsvakt i stället för
  // navigator.onLine, som ljuger i appens webbvy.
  if (typeof window !== "undefined") {
    onlineManager.setEventListener((setOnline) => {
      startConnectivityWatch();
      setOnline(getConnectivity().state === "online");
      return subscribeConnectivity((value) => setOnline(value.state === "online"));
    });
  }

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    // På mobil rullar innehållet i #app-scroll (app-skalet), inte fönstret.
    scrollToTopSelectors: ["#app-scroll"],
    // Kom ihåg scrollpositionen per sida (inte per sökparameter), så att sidan
    // inte hoppar upp när man t.ex. skriver i sökrutan eller byter filter.
    getScrollRestorationKey: (location) => location.href,

    // Förladda annonsen redan när man hovrar/trycker på kortet – annars känns
    // klicket segt eftersom sidan hämtar allt först efteråt.
    defaultPreload: "intent",
    // På mobil finns ingen hover – förladdningen startar vid touchstart och ska
    // inte vänta extra, annars känns navigeringen i "mina sidor" seg.
    defaultPreloadDelay: 0,
    defaultPreloadStaleTime: 60_000,
    // Visa laddningsläget snabbt så att ett tryck alltid känns som att något händer.
    defaultPendingMs: 80,
    defaultPendingMinMs: 300,
  });

  // Skickar med data som hämtats på servern till webbläsaren, så att sidan
  // visar samma innehåll direkt vid start (och slipper ladda om allt).
  setupRouterSsrQueryIntegration({ router, queryClient });

  return router;
};
